/*
  This is an exceptional case where a cycical dependency breaks mobile. If we import the portal gate to the foreground
  here, it will break. The bundler will give up and replace stuff with `undefined`.
  This actually imports:
  - Mobile: `shared/foreground/database/index.ts`.
  - Everything else: `shared/background/databaseSpecialCase.ts` (which exports the background database)
*/
import database from '_database-special-case';

import clearLocalRxdbData from '../database/clearLocalRxdbData.platform';
import { removeAllDatabases } from '../database/internals/migration/helpers.platform';
// eslint-disable-next-line restrict-imports/restrict-imports
import type { AnyDocumentWithContent } from '../foreground/documentSearchEngine';
// eslint-disable-next-line restrict-imports/restrict-imports
import type { StoreItemEventArgument, StoreItemEventName, StoreName } from '../foreground/types/events';
// eslint-disable-next-line import/no-cycle,restrict-imports/restrict-imports
import networkDetector from '../networkDetector.platform';
import reloadApplication from '../reloadApplication.platform';
import {
  CacheDocumentLocation,
  ContentParsingStatus,
  DocumentContent,
  FirstClassDocument,
  isAudiobookDocumentContent,
  OngoingOperationsByLocation,
  Profile,
  RSSSuggestion,
  StateSyncingOptions,
  UserEventWithDataUpdate,
} from '../types';
import type { DocumentChunkManifest } from '../types/chunkedDocuments';
import { notEmpty } from '../typeValidators';
// eslint-disable-next-line import/no-cycle
import { clearAllBookwiseLicenseTokens } from '../utils/bookwiseLicenseTokens.platform';
import nowTimestamp from '../utils/dates/nowTimestamp';
import delay from '../utils/delay';
// eslint-disable-next-line import/no-cycle
import { forceReRegister } from '../utils/deviceKeyRegistration.platform';
import { isUnlicensedOrLicenseValid } from '../utils/drm';
import { isBookwise, isDesktopApp, isExtension, isMobile, isTest, isWebApp } from '../utils/environment';
import exceptionHandler from '../utils/exceptionHandler.platform';
import getServerBaseUrl from '../utils/getServerBaseUrl.platform';
import HttpError from '../utils/HttpError';
import makeLogger from '../utils/makeLogger';
import promiseAllSettled from '../utils/promiseAllSettled.platform';
import requestWithAuth from '../utils/requestWithAuth.platformIncludingExtension';
import { shouldIncludeDocumentInSearch } from '../utils/shouldIncludeDocumentInSearch';
// eslint-disable-next-line import/no-cycle
import * as audiobookTracks from './audiobookTrackLoading';
import { Cache as OurCache } from './cache.platform';
import { CollectionSyncer } from './collectionSyncer';
import * as documentChunks from './documentChunkLoading';
import onNextUserInteraction from './onNextUserInteraction.platform';
// eslint-disable-next-line import/no-cycle
import foreground, { portalGate as foregroundPortalGate } from './portalGates/toForeground';
// eslint-disable-next-line import/no-cycle
import { fixAllGlobalTagsLastUpdatedAt, setupPeriodicGlobalTagsRefresh } from './refreshGlobalTags';
import { StateSyncer } from './stateSyncing';
import stores from './stores';
import * as ttsWordBoundaries from './ttsWordBoundaryLoading';

export * from './documentChunkLoading';
export * from './ttsWordBoundaryLoading';
export * from './audiobookTrackLoading';
export * from './decryptAudiobookFileMap';

const logger = makeLogger(__filename, { shouldLogToNewRelic: true });

let stateSyncer: StateSyncer;

let trackedBookSyncer: CollectionSyncer<'tracked_books'>;

let isAllowedToFetchAvailableRssFeeds = false;
const lastFetchRelatedRSSCallArguments: string[] = [];

let profileData: Profile;


export const init = async (options: Omit<StateSyncingOptions, 'database'>): Promise<void> => {
  await database.initialize({
    onSchemaConflictError: foreground.onDatabaseSchemaConflict,
  });

  // Always use our background's database for syncing.
  stateSyncer = new StateSyncer({ ...options, database });

  stateSyncer.onPersistentStateLoadedFromServer = async () => {
    logger.debug('onPersistentStateLoadedFromServer called');
    // TODO: really this should wait until hybrid storage replication is done.
    await delay(15000);
    logger.debug('Loading all document content');
    await loadDocumentContentInBackground();
    documentChunks.loadDocumentChunksInBackground();

    if (isWebApp || isDesktopApp) {
    // One-time fix of `lastAssignedAt` values on global_tags to fix wrong values (runs on all platforms, gated by cache key)
      try {
        await fixAllGlobalTagsLastUpdatedAt(database);
      } catch (error) {
        const errorMessage = 'Error rebuilding global tags';
        logger.error(errorMessage, { error });
        exceptionHandler.captureException(error instanceof Error ? error : new Error(String(error)), {
          extra: { errorMessage },
        });
      }
    }

    if (isDesktopApp) {
      try {
        await setupPeriodicGlobalTagsRefresh(database);
      } catch (error) {
        const errorMessage = 'Error setting up periodic GlobalTags refresh';
        logger.error(errorMessage, { error });
        exceptionHandler.captureException(error instanceof Error ? error : new Error(String(error)), {
          extra: { errorMessage },
        });
      }
    }
  };

  // Listen to all store events and emit events across the foreground portal gate
  const foregroundGate = foregroundPortalGate as NonNullable<typeof foregroundPortalGate>;
  for (const [name, store] of Object.entries(stores)) {
    store.onAny((event: string | string[], data: unknown) => {
      // claude eslint: name is string from Object.entries, event is string|string[] from EventEmitter2,
      // data is unknown — all narrowed to their known runtime types for the template literal emit call
      foregroundGate.emit(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        `stores__${name as StoreName}__${event as StoreItemEventName}`, // claude eslint: name is a StoreName key from Object.entries(stores); event is always a StoreItemEventName string from EventEmitter2 onAny
        data as StoreItemEventArgument, // eslint-disable-line @typescript-eslint/no-unsafe-type-assertion -- claude eslint: data is the event argument matching StoreItemEventArgument for the given event name
      );
    });
  }

  if (isExtension) {
    return;
  }

  // Let's avoid impacting the initial load / performance too much
  // eslint-disable-next-line promise/catch-or-return
  delay().then(async () => {
    if (!isMobile) {
      // Lighthouse, Calibre, etc. will notice if we only use a timeout and lower our perf score
      await onNextUserInteraction();
    }

    await delay(3000);
    await setUpRelatedRSS();
  });
};

export const getCacheItem = OurCache.getItem;
export const setCacheItem = OurCache.setItem;
export const getCacheKeys = OurCache.keys;
export const removeCacheItems = OurCache.removeItems;

// NOTE: this uses the Cache Storage API, not our typical cache
type CachePut = (typeof Cache)['prototype']['put'];
export const putItemInCacheStorage = async (
  cacheName: Parameters<typeof caches.open>[0],
  request: Parameters<CachePut>[0],
  responseBody: ConstructorParameters<typeof Response>[0],
): ReturnType<CachePut> => {
  const cache = await caches.open(cacheName);
  return cache.put(request, new Response(responseBody));
};

export const triggerCloudSyncs = async (): Promise<void> => {
  const options: RequestInit & { headers: { [key: string]: string; }; } = {
    credentials: 'include',
    headers: {},
    method: 'GET',
    mode: 'cors',
  };
  try {
    await requestWithAuth(`${getServerBaseUrl()}/reader/api/trigger_cloud_syncs/`, options);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    if (!e?.response?.status || e?.response?.status !== 403) {
      throw e;
    }
  }
};

export const uploadInstapaperCSV = async (file: FormData): Promise<Response> => {
  const resp = await requestWithAuth(`${getServerBaseUrl()}/reader/instapaper_csv/`, {
    credentials: 'include',
    method: 'POST',
    mode: 'cors',
    body: file,
  });

  if (!resp.ok) {
    const exception = new HttpError(resp);
    await exception.getReasonFromJson();
    throw exception;
  }

  return resp;
};

export const getRssSuggestions = async (
  query: string,
  signal?: AbortSignal,
): Promise<RSSSuggestion[]> => {
  const resp = await requestWithAuth(`${getServerBaseUrl()}/reader/api/rss_feed_search?query=${query}`, {
    credentials: 'include',
    method: 'GET',
    signal,
  });
  if (!resp.ok) {
    const exception = new HttpError(resp);
    await exception.getReasonFromJson();
    throw exception;
  }
  return resp.json();
};

export const getUserFeedSuggestions = async (): Promise<RSSSuggestion[]> => {
  const resp = await requestWithAuth(`${getServerBaseUrl()}/reader/api/suggested_feeds`, {
    credentials: 'include',
    method: 'GET',
  });
  if (!resp.ok) {
    const exception = new HttpError(resp);
    await exception.getReasonFromJson();
    throw exception;
  }
  return resp.json();
};

export const clearAllLocalData = async (shouldReloadApplication = true): Promise<void> => {
  // Don't clear the cache while there's any content in the queues -- that'll break stuff!
  if (stateSyncer) {
    stateSyncer.syncingStopped = true;
    let timeWaiting = 0;
    while (!stateSyncer.safeToInterrupt() && timeWaiting < 5000) {
      await delay(100);
      timeWaiting += 100;
    }
  }

  await foreground.disableAllDatabaseHooks();

  await promiseAllSettled([
    removeAllDatabases(),
    OurCache.clear(),
    ...Object.values(stores).map((store) => store.clearCache()),
    documentChunks.clearCache(),
    audiobookTracks.clearAudiobookCache(),
    ttsWordBoundaries.clearTtsWordBoundariesCache(),
    foreground.searchDestroy(),
    Promise.resolve(clearAllBookwiseLicenseTokens()),
  ]);
  await clearLocalRxdbData();

  if (!isExtension && shouldReloadApplication) {
    await reloadApplication();
  }
};

export const getCacheDataForDebugging = async () => {
  return stateSyncer.getCacheDataForDebugging();
};

export const downloadSyncerCacheForDebugging = async (): Promise<void> => {
  stateSyncer.downloadCacheForDebugging();
};

export const loadDocumentContentByIds = (
  ...args: Parameters<typeof stores.documentContent.loadByIds>
): ReturnType<typeof stores.documentContent.loadByIds> => stores.documentContent.loadByIds(...args);

export const getCachedAudiobookTracks = async (manifest: Parameters<typeof audiobookTracks.getLocallyCachedTracks>[0]): ReturnType<typeof audiobookTracks.getLocallyCachedTracks> => {
  return audiobookTracks.getLocallyCachedTracks(manifest);
};

export async function clearAllDocumentContentStores(): Promise<void> {
  await Promise.all([
    stores.documentContent.clearCache(),
    documentChunks.clearCache(),
    audiobookTracks.clearAudiobookCache(),
    ttsWordBoundaries.clearTtsWordBoundariesCache(),
  ]);
}

export async function clearDeviceEncryptedContentCaches(): Promise<void> {
  logger.info('Clearing device-encrypted content caches');
  await clearAllDocumentContentStores();
  logger.info('Device-encrypted content caches cleared');
}

export async function forceReRegisterWithCacheClear(profileId: number): Promise<void> {
  await clearDeviceEncryptedContentCaches();
  await forceReRegister(profileId);
}

export const removeDocumentContentByIds = async (...id: string[]): Promise<void> => {
  await stores.documentContent.purgeByIds(...id);
};

// TODO: remove the following variable once we're loading all content
let hasCalledPopulateDocumentSearchEngine = false;

function isUsingInternalDebuggingTool(): boolean {
  return !isMobile && window.location.pathname.endsWith('/database-explorer');
}

export async function loadDocumentContentInBackground(): Promise<void> {
  if (
    hasCalledPopulateDocumentSearchEngine ||
    stores.documentContent.hasLoadedAll ||
    isUsingInternalDebuggingTool()
  ) {
    return;
  }
  logger.debug('loadDocumentContentInBackground start');
  hasCalledPopulateDocumentSearchEngine = true;
  let totalIndexed = 0;
  const onDocumentContentsReceived = async ({ items }: { items: DocumentContent[]; }) => {
    // `items` will have at most 120 items in it, since that's the page limit of the document content API endpoint.
    // That's few enough so that indexing them in bulk is safe i.e. it won't freeze up the main thread.
    const timerId = Math.random().toString().slice(2);
    const parsedDocIds = items.map(({ id }) => id);
    const parsedDocIdToContentMap = Object.fromEntries(items.map((item) => [item.id, item]));
    logger.time(`loadDocumentContentInBackground findDocsForParsedDocIds ${timerId}`);
    const docsWithParsedDocId = await foreground.findDocsForParsedDocIds(parsedDocIds);
    logger.timeEnd(`loadDocumentContentInBackground findDocsForParsedDocIds ${timerId}`);

    const docsToIndex: FirstClassDocument[] = items
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .map(({ id: parsed_doc_id, status, html: html_content }) => {
        const doc = docsWithParsedDocId[parsed_doc_id]?.[0];
        if (status !== ContentParsingStatus.Success || !doc || !shouldIncludeDocumentInSearch(doc)) {
          return null;
        }
        const docToUpsert: AnyDocumentWithContent = {
          ...doc,
          html_content,
        };
        return docToUpsert;
      })
      .filter(notEmpty);

    if (docsToIndex.length === 0) {
      logger.debug('loadDocumentContentInBackground no items to index, skipping', {
        itemBatchCount: items.length,
        parsedDocIds,
      });
      return;
    }
    const queued: [string, number][] = [];
    for (const doc of docsToIndex) {
      if (!doc.parsed_doc_id) {
        continue;
      }
      const content = parsedDocIdToContentMap[doc.parsed_doc_id];
      if (!content) {
        continue;
      }

      if ('manifest' in content && content.manifest && isUnlicensedOrLicenseValid(content)) {
        if (isAudiobookDocumentContent(content) && isBookwise) {
          queued.push([doc.id, Object.keys(content.manifest).length]);
          const encryptedFileMap = content.encrypted_file_map;
          const encryptedTrackCount = encryptedFileMap ? Object.keys(encryptedFileMap).length : 0;
          const profileId = await foreground.getProfileId();
          logger.info('Background loading audiobook manifest from document_content', {
            docId: doc.id,
            hasEncryptedTracks: encryptedTrackCount > 0,
            encryptedTrackCount,
            trackCount: Object.keys(content.manifest).length,
          });
          await audiobookTracks.queueLoadForAudiobookManifest(doc.id, String(doc.parsed_doc_id), content.manifest, encryptedFileMap, profileId);
        } else {
          // claude eslint: content.manifest is AudiobookManifest | DocumentChunkManifest (discriminated by
          // isAudiobookDocumentContent check above); in the else branch it is DocumentChunkManifest.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const chunkManifest = content.manifest as DocumentChunkManifest;
          queued.push([doc.id, Object.keys(chunkManifest).length]);
          await documentChunks.queueLoadForChunksFromManifest(doc.id, chunkManifest);
        }
      }
    }
    logger.debug('loadDocumentContentInBackground queueChunkLoadForDoc', { queued });
    logger.time(`loadDocumentContentInBackground searchUpsertDocuments ${timerId}`);
    await foreground.searchUpsertDocuments(docsToIndex);
    logger.timeEnd(`loadDocumentContentInBackground searchUpsertDocuments ${timerId}`);
    totalIndexed += docsToIndex.length;

    logger.debug('loadDocumentContentInBackground after searchUpsertDocuments', {
      parsedDocIds,
      docsToIndex: docsToIndex.length,
      totalIndexed,
    });
  };

  await foreground.searchInit();
  // From here on, if any new items are fetched and loaded into cache, push them into search
  stores.documentContent.on('items-received-from-server', onDocumentContentsReceived);

  await stores.documentContent.initializeLoading({ isInitialLoad: true });
  logger.info('Completed initial document content background load kick-off');
}

const setUpRelatedRSS = async () => {
  isAllowedToFetchAvailableRssFeeds = true;
  if (lastFetchRelatedRSSCallArguments.length) {
    stores.knownRssFeeds.loadByIds(lastFetchRelatedRSSCallArguments);
  }
  stores.knownRssFeeds.on(
    'items-received-by-id',
    ({
      items,
      missingIds,
    }: {
      items: RSSSuggestion[];
      missingIds: string[];
    }) => {
      foreground.onRSSFeedsLoaded(items, missingIds);
    },
  );
};

export const loadRelatedRSSFeedsForDomains = async (domains: string[]): Promise<void> => {
  if (!isAllowedToFetchAvailableRssFeeds) {
    lastFetchRelatedRSSCallArguments.push(...domains);
    return Promise.reject(new Error('Not allowed yet. Will fetch once allowed.'));
  }
  await stores.knownRssFeeds.loadByIds(domains);
};

// Syncing helper functions:

export const pollLatestState = async (
  timesToPoll: number,
  shouldTriggerCloudSyncs = false,
): Promise<void> => {
  // If user is offline, try later
  if (networkDetector.isConfirmedOffline) {
    setTimeout(() => {
      pollLatestState(timesToPoll, shouldTriggerCloudSyncs);
    }, 1500);
    return;
  }

  if (shouldTriggerCloudSyncs) {
    triggerCloudSyncs();
  }

  if (stateSyncer) {
    // TODO: timesToPoll is just a hack until we can know when an instapaper/pocket sync is done
    await stateSyncer.syncServerStateForNext(5);
  }
  if (timesToPoll > 1) {
    setTimeout(() => pollLatestState(timesToPoll - 1), 1500);
  }
};

export const setUpInitialState = async (): ReturnType<StateSyncer['setUpInitialState']> => {
  if (!stateSyncer) {
    throw new Error('stateSyncer does not exist yet!');
  }
  return stateSyncer.setUpInitialState();
};

export const queueStateUpdateFromForeground = async (event: UserEventWithDataUpdate): Promise<void> => {
  return stateSyncer.queueStateUpdateFromForeground(event);
};

export const getAndSetProfileData = async (): Promise<Profile> => {
  const urlSearchParams = new URLSearchParams();
  if (isBookwise) {
    urlSearchParams.append('bookwise', 'true');
  }
  const resp = await requestWithAuth(`${getServerBaseUrl()}/reader/api/profile_details/?${urlSearchParams}`, {
    credentials: 'include',
    method: 'GET',
  });
  if (!resp.ok) {
    const exception = new HttpError(resp);
    await exception.getReasonFromJson();
    throw exception;
  }

  profileData = await resp.json();
  foreground.setProfile(profileData, { userInteraction: null });
  exceptionHandler.setUser({ id: profileData.profile_id, email: profileData.email });

  return profileData;
};

// Utility functions just for testing:
export const _syncingTestHelper = (action: string) => {
  if (!isTest) {
    throw new Error('Can only use _testHelper in tests');
  }
  logger.debug(`stateSyncing:${action}`);

  if (!stateSyncer) {
    logger.warn("Can't apply syncing test helper since stateSyncer is not initialized yet.");
    return;
  }

  if (action === 'stopSyncing') {
    stateSyncer.syncingStopped = true;
  } else if (action === 'resumeSyncing') {
    stateSyncer.syncingStopped = false;
    stateSyncer.consumeUpdateQueues();
  }
};

export const checkIfNonceExists = async (nonce: string) => {
  const resp = await requestWithAuth(`${getServerBaseUrl()}/reader/api/nonce_exists?nonce=${nonce}`, {
    credentials: 'include',
    method: 'GET',
    mode: 'cors',
  });
  if (resp.ok) {
    const data = await resp.json();
    return data.exists;
  }
};

export const tellStateSyncerThatAppFocusChanged = (isFocused: boolean) => {
  if (stateSyncer) {
    stateSyncer.onAppFocusChange(isFocused);
  }
};

export const getCachedDocumentContentIds = async (): Promise<string[]> => {
  return stores.documentContent.getCachedItemIds();
};

export const getOngoingDocumentContentOperations = async (): Promise<OngoingOperationsByLocation> => {
  return stores.documentContent.getOngoingOperations();
};

export const isLocationSyncInitial = async (location: CacheDocumentLocation): Promise<boolean> => {
  return stores.documentContent.isLocationSyncInitial(location);
};

export const reinitializeContentLoading = async (): Promise<void> => {
  return stores.documentContent.initializeLoading({ isInitialLoad: false });
};

export const setLocationContentEnabled = async (
  location: CacheDocumentLocation,
  enabled: boolean,
): Promise<void> =>
  stores.documentContent.setLocationEnabled(location, enabled);

// This interval ensures that we don't trigger content loading on frequent focus changes
const CONTENT_LOAD_INTERVAL_MS = 1000 * 60 * 5; // 5 minutes

/**
 * Triggers background loading of document content and chunks.
 */
export const considerPollingLatestDocumentContent = (() => {
  let lastContentLoadTriggeredAt = nowTimestamp();

  return () => {
    const timeSinceLastLoad = nowTimestamp() - lastContentLoadTriggeredAt;

    if (timeSinceLastLoad > CONTENT_LOAD_INTERVAL_MS) {
      lastContentLoadTriggeredAt = nowTimestamp();
      logger.debug('Triggering document content loading on app focus', {
        timeSinceLastLoad: timeSinceLastLoad / 1000,
      });

      // Small delay to ensure the UI has time to update
      setTimeout(() => {
        reinitializeContentLoading();
        documentChunks.loadDocumentChunksInBackground();
      }, 3000);
    } else {
      logger.debug('Skipping document content load, too recent', {
        timeSinceLastLoad: timeSinceLastLoad / 1000,
        threshold: CONTENT_LOAD_INTERVAL_MS / 1000,
      });
    }
  };
})();

export async function beginSyncingTrackedBooks(initialSync = false) {
  if (!isBookwise) {
    return;
  }
  if (!trackedBookSyncer) {
    trackedBookSyncer = new CollectionSyncer(database.getRawCollection('tracked_books'), 'bookwise/api/tracked_books');
    trackedBookSyncer.beginSyncing({}, initialSync);
  } else {
    return resyncTrackedBooks();
  }
}

export async function resyncTrackedBooks() {
  if (!trackedBookSyncer) {
    return;
  }
  trackedBookSyncer.resync();
}

export async function resyncTrackedBooksAndAwait(): Promise<void> {
  if (!trackedBookSyncer) {
    return;
  }
  await trackedBookSyncer.resyncAndAwait();
}
